import { atom, useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import { useEffect } from 'react';
import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';
import {
  SysChkValidator,
  SysChkValidatorEnforcement,
  SystemCheckValidators,
  validateAll,
} from './validators';
import { logger } from 'modules/logger';
import { env } from 'config/env';

import type { RecoilState } from 'recoil';
import type { SysChkValidatorTypes, SystemCheckValidatorsMap } from './validators';

/** Sentinel renderer string written into the GPU bypass when running under
 *  Playwright/CI. `deriveShouldBypassSystemCheck` treats it as a wildcard so
 *  the bypass holds regardless of which renderer the headless browser reports
 *  (typically SwiftShader, llvmpipe, or similar — all on the denylist). */
export const E2E_BYPASS_SENTINEL = '__e2e_bypass__';

const isE2ETestEnvironment = (): boolean => env.E2E_TEST === 'true';

export type ValidationResult = {
  id: SysChkValidatorTypes;
  result: boolean;
  reason: Nullable<string>;
  metadata?: any;
};

/**
 * Per-validator bypass state. The GPU bypass also stores the renderer string
 * captured at acceptance time so that swapping hardware re-prompts the user
 * instead of silently keeping the old acknowledgement.
 */
export type SystemCheckBypassState = {
  extension: boolean;
  hardwareAcceleration: boolean;
  gpu: {
    accepted: boolean;
    rendererAtAcceptance: Nullable<string>;
  };
};

export type SystemCheckPreferenceState = {
  bypass: SystemCheckBypassState;
};

export const DEFAULT_BYPASS: SystemCheckBypassState = {
  extension: false,
  hardwareAcceleration: false,
  gpu: { accepted: false, rendererAtAcceptance: null },
};

export const systemCheckPreferenceState: RecoilState<SystemCheckPreferenceState> =
  atom<SystemCheckPreferenceState>({
    key: 'systemCheckPreferenceState',
    default: { bypass: DEFAULT_BYPASS },
    // v2 introduced the per-validator bypass record (was a single boolean);
    // v3 added the hardwareAcceleration flag. Stale entries are auto-removed
    // by the persister.
    effects: [localStoragePersisterEffect({ version: 3 }), broadcastChannelSynchronizerEffect()],
  });

export type SystemCheckState = {
  isSystemValid: boolean;
  validationResults: ValidationResult[];
  /** Flag that controls the display of the system check screen */
  displaySystemCheckModal: boolean;
  /** Timestamp of when the last system check was executed */
  lastCheckTimestamp: number;
};

/** Merge function to always accept only the most recent system check results */
const systemCheckMergeFn = (
  oldValue: Nullable<SystemCheckState>,
  newValue: SystemCheckState
): SystemCheckState => {
  if (oldValue == null) {
    return newValue;
  } else if (newValue.lastCheckTimestamp >= oldValue?.lastCheckTimestamp) {
    return newValue;
  }
  return oldValue;
};

export const systemCheckState: RecoilState<SystemCheckState> = atom({
  key: 'systemCheckState',
  default: {
    isSystemValid: false,
    validationResults: [],
    displaySystemCheckModal: false,
    lastCheckTimestamp: 0,
  },
  effects: [broadcastChannelSynchronizerEffect({ mergeFn: systemCheckMergeFn })],
});

/**
 * Returns true iff every currently-failing **Blocking** validator has a
 * matching bypass. Recommended validators (HardwareAcceleration, GpuCapability)
 * are intentionally excluded — they never require an explicit user bypass.
 */
export const deriveShouldBypassSystemCheck = (
  results: ReadonlyArray<ValidationResult>,
  bypass: SystemCheckBypassState
): boolean => {
  const failingBlocking = results.filter(
    (r) =>
      r.result === false &&
      SystemCheckValidators.get(r.id)?.enforcement === SysChkValidatorEnforcement.Blocking
  );
  if (failingBlocking.length === 0) return false;

  return failingBlocking.every((r) => {
    switch (r.id) {
      case SysChkValidator.ExtensionInstallState:
      case SysChkValidator.ExtensionMinVersion:
        return bypass.extension;
      default:
        return false;
    }
  });
};

/** Hook to initiate system checks and for returning the current state of validations */
export const useSystemCheck = (
  systemCheckValidators: SystemCheckValidatorsMap
): {
  /** Runs all enabled validators to produce a single result */
  validate: () => {
    isSystemValid: boolean;
  };
  /** Resets the validation results */
  reset: () => void;
  /**
   * Apply or clear bypass for every currently-failing validator. For GPU, the
   * current renderer string is captured as the acceptance fingerprint.
   */
  setBypassSystemCheck: (shouldBypass: boolean) => void;
  isSystemValid: boolean;
  shouldBypassSystemCheck: boolean;
  validationResults: ValidationResult[];
} => {
  const { isSystemValid, validationResults } = useRecoilValue(systemCheckState);
  const { bypass } = useRecoilValue(systemCheckPreferenceState);

  const shouldBypassSystemCheck = deriveShouldBypassSystemCheck(validationResults, bypass);

  const setBypassSystemCheck = useRecoilCallback(({ set, snapshot }) => (shouldBypass: boolean) => {
    const currentResults = snapshot.getLoadable(systemCheckState).valueOrThrow().validationResults;
    set(systemCheckPreferenceState, (prevState) => {
      if (!shouldBypass) {
        return { ...prevState, bypass: DEFAULT_BYPASS };
      }
      const next: SystemCheckBypassState = {
        ...prevState.bypass,
        gpu: { ...prevState.bypass.gpu },
      };
      for (const r of currentResults) {
        if (r.result) continue;
        if (
          r.id === SysChkValidator.ExtensionInstallState ||
          r.id === SysChkValidator.ExtensionMinVersion
        ) {
          if (!prevState.bypass.extension) {
            logger.warn(`[SysCheck] User accepted extension bypass; validator: "${r.id}"`);
          }
          next.extension = true;
        }
      }
      return { ...prevState, bypass: next };
    });
  });

  const validate = useRecoilCallback<
    [],
    {
      isSystemValid: boolean;
    }
  >(({ set }) => () => {
    const { result, validators } = validateAll();
    set(systemCheckState, (state) => ({
      ...state,
      isSystemValid: result,
      validationResults: validators,
      lastCheckTimestamp: Date.now(),
    }));

    return { isSystemValid: result };
  });

  const reset = useRecoilCallback(({ set }) => () => {
    set(systemCheckState, (state) => ({
      ...state,
      isSystemValid: false,
      validationResults: [],
    }));
  });

  return {
    validate,
    setBypassSystemCheck,
    isSystemValid,
    shouldBypassSystemCheck,
    validationResults,
    reset,
  };
};

/** Auto-bypass the entire system check when the app is running under a CI
 *  e2e harness (PUBLIC_E2E_TEST=true). Headless test runners do not have the
 *  Workspace Manager extension installed and typically expose a software
 *  renderer (SwiftShader / llvmpipe) that lands on the GPU denylist, so the
 *  modal would otherwise block every Playwright run. Sets both the extension
 *  bypass and a sentinel GPU bypass that `deriveShouldBypassSystemCheck`
 *  treats as a wildcard match for any renderer. */
export const useAutoBypassForE2E = (): void => {
  const setSysCheckPrefs = useSetRecoilState(systemCheckPreferenceState);

  useEffect(() => {
    if (!isE2ETestEnvironment()) return;
    setSysCheckPrefs((prev) => {
      const alreadyBypassed =
        prev.bypass.extension &&
        prev.bypass.hardwareAcceleration &&
        prev.bypass.gpu.accepted &&
        prev.bypass.gpu.rendererAtAcceptance === E2E_BYPASS_SENTINEL;
      if (alreadyBypassed) return prev;
      logger.info('[SysCheck] Auto-bypassing system check (PUBLIC_E2E_TEST=true)');
      return {
        ...prev,
        bypass: {
          extension: true,
          hardwareAcceleration: true,
          gpu: { accepted: true, rendererAtAcceptance: E2E_BYPASS_SENTINEL },
        },
      };
    });
  }, [setSysCheckPrefs]);
};

/** Hook for managing the visibility of the system check */
export const useDisplaySystemCheck = (): {
  /** Default handler for the System Check component; hides the modal */
  handleOnContinue: () => void;
  shouldShowSystemCheck: boolean;
  showSystemCheckScreen: () => void;
} => {
  const { displaySystemCheckModal } = useRecoilValue(systemCheckState);

  const handleOnContinue = useRecoilCallback(({ set }) => () => {
    set(systemCheckState, (prevState) => ({ ...prevState, displaySystemCheckModal: false }));
  });

  const showSystemCheckScreen = useRecoilCallback(({ set }) => () => {
    set(systemCheckState, (prevState) => ({ ...prevState, displaySystemCheckModal: true }));
  });

  return {
    shouldShowSystemCheck: displaySystemCheckModal,
    handleOnContinue,
    showSystemCheckScreen,
  };
};
